/*eslint max-lines-per-function: ["error", 280]*/

import type { TFunction } from 'i18next';

import {
  DefaultApi,
  GetDelayedProjectDto,
  GetLastEditorDto,
  GetOrderDto,
  GetProjectDto,
  GetProjectExtendedDto,
} from '../autogenerated/pokApiClient';
import {
  CreateUpdateProjectWithTeamDto,
  CreateUpdateProjectWithTeamDtoFromJSON,
} from '../autogenerated/pokApiClient';
import { CloneProjectSchema, ProjectSchema } from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { projectForGrid } from '../../../pok/components/Projects/ProjectSearch';
import * as AttachmentContext from '../../../common/pokCore/contexts/AttachmentContext';
import { PokCoreContextType } from '../pokCore';
import { NotificationsProviderType } from '../../notifications';
import {
  CloneProjectDto,
  CloneProjectDtoFromJSON,
} from '../autogenerated/pokApiClient/models/CloneProjectDto';
import { defaultSelectorLimit } from '../../../pok/components/Selects/Selector';
import {
  getSummary,
  projectFinanceForGrid,
} from '../../../pok/components/Projects/ProjectFinanceSearch';
import { purchaseInvoiceProjectForGrid } from '../../../pok/components/PurchaseInvoices/PurchaseInvoiceProjectsSearch';
import { TKeys } from '../../hooks/useTranslation';
import { AttachmentObjectType } from '../validation/enums';

export interface ProjectContextInterface {
  getById: (id: string) => Promise<GetProjectDto>;
  getExtendedById: (id: string) => Promise<GetProjectExtendedDto>;
  getAllByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllForPurchaseInvoiceByClientAndCompanyByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    clientId?: string,
    idsToExclude?: string[],
  ) => Promise<GridRecord[]>;
  getAllForPurchaseInvoiceByClientAndCompanyCount: (
    filterText?: string,
    clientId?: string,
    idsToExclude?: string[],
  ) => Promise<number>;
  getAllForPurchaseInvoiceByPurchaseInvoice: (
    purchaseInvoiceId: string,
  ) => Promise<GetProjectExtendedDto[]>;
  getAllReadyToCorrectionByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getAllReadyToInvoiceByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    clientId?: string,
    budgetId?: string,
    idsToExclude?: string[],
    hidePurchaserClient?: boolean,
    all?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<GridRecord[]>;
  getAllReadyToInvoiceCount: (
    filterText?: string,
    clientId?: string,
    budgetId?: string,
    idsToExclude?: string[],
    all?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<number>;
  getAllCorrectedByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<GridRecord[]>;
  getAllCorrectedCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<number>;
  getAllReadyToCorrectionCount: (filterText?: string) => Promise<number>;
  getForFinanceByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    dateFrom?: Date | null,
    dateTo?: Date | null,
  ) => Promise<GridRecord[]>;
  getForFinanceCount: (
    filterText?: string,
    filterColumns?: string,
    dateFrom?: Date | null,
    dateTo?: Date | null,
  ) => Promise<number>;
  getSumForFinance: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    filterText?: string,
    filterColumns?: string,
    dateFrom?: Date | null,
    dateTo?: Date | null,
  ) => Promise<GridRecord>;
  create: (dto: CreateUpdateProjectWithTeamDto) => Promise<GetProjectDto>;
  update: (id: string, dto: CreateUpdateProjectWithTeamDto) => Promise<void>;
  isDelayed: (date: Date) => Promise<GetDelayedProjectDto>;
  findInternalProjectsByPurchaserForCompanyAndTeams: (
    purchaserId: string,
    teamsIds: string[],
  ) => Promise<GetProjectDto[]>;
  getWithParent: (id: string) => Promise<GetProjectDto>;
  getUniqueServicesNames: (id: string) => Promise<string[]>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  clone: (clone: CloneProjectDto) => Promise<GetProjectDto>;
  getAllByFilter: (
    text?: string,
    companyId?: string,
  ) => Promise<GetProjectDto[]>;
  getWarningsWithFVZ: (
    id: string,
    month?: Date,
    profileId?: string,
  ) => Promise<string[]>;
  isApplicationRequiredForDateChange(
    project: CreateUpdateProjectWithTeamDto,
    startDate: Date | undefined,
    endDate: Date | undefined,
  ): Promise<boolean>;
}

export const ProjectContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.projectControllerGet(id),
    getExtendedById: (id: string) => api.projectControllerGetExtended(id),
    getAllByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.projectControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(data => projectForGrid(t, tk, data, true, true));
    },
    getAllForPurchaseInvoiceByClientAndCompanyByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      clientId?: string,
      idsToExclude?: string[],
    ) => {
      const data =
        await api.projectControllerGetAllForPurchaseInvoiceByClientAndCompanyByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          clientId || '',
          idsToExclude,
        );
      return data.map(purchaseInvoiceProjectForGrid);
    },
    getAllForPurchaseInvoiceByPurchaseInvoice: (purchaseInvoiceId: string) =>
      api.projectControllerGetAllForPurchaseInvoiceByPurchaseInvoice(
        purchaseInvoiceId,
      ),
    getAllReadyToCorrectionByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      const data = await api.projectControllerGetAllReadyToCorrectionByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
      );

      return data.map(data => projectForGrid(t, tk, data));
    },
    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.projectControllerGetAllCount(filterText || '', filterColumns),
    getAllReadyToInvoiceByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      clientId?: string,
      budgetId?: string,
      idsToExclude?: string[],
      hidePurchaserClient?: boolean,
      all?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
    ) => {
      const data = await api.projectControllerGetAllReadyToInvoiceByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        clientId || '',
        budgetId || '',
        idsToExclude,
        all,
        dateFrom,
        dateTo,
      );
      return data.map(data =>
        projectForGrid(t, tk, data, false, false, hidePurchaserClient),
      );
    },
    getAllReadyToInvoiceCount: (
      filterText?: string,
      clientId?: string,
      budgetId?: string,
      idsToExclude?: string[],
      all?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
    ) =>
      api.projectControllerGetAllReadyToInvoiceCount(
        filterText || '',
        clientId || '',
        budgetId || '',
        idsToExclude,
        all,
        dateFrom,
        dateTo,
      ),
    getAllCorrectedByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
    ) => {
      const data = await api.projectControllerGetAllCorrectedByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
      );
      return data.map(data => projectForGrid(t, tk, data, false, false, false));
    },
    getAllCorrectedCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
    ) =>
      api.projectControllerGetAllCorrectedCount(
        filterText || '',
        dateFrom,
        dateTo,
      ),
    getAllReadyToCorrectionCount: (filterText?: string) =>
      api.projectControllerGetAllReadyToCorrectionCount(filterText || ''),
    getAllForPurchaseInvoiceByClientAndCompanyCount: (
      filterText?: string,
      clientId?: string,
      idsToExclude?: string[],
    ) =>
      api.projectControllerGetAllForPurchaseInvoiceByClientAndCompanyCount(
        filterText || '',
        clientId || '',
        idsToExclude,
      ),
    getForFinanceByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      dateFrom?: Date | null,
      dateTo?: Date | null,
    ) => {
      const data = await api.projectControllerGetForFinanceByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
        dateFrom || undefined,
        dateTo || undefined,
      );

      return data.map(data => projectFinanceForGrid(t, tk, data));
    },
    getForFinanceCount: (
      filterText?: string,
      filterColumns?: string,
      dateFrom?: Date | null,
      dateTo?: Date | null,
    ) =>
      api.projectControllerGetForFinanceCount(
        filterText || '',
        filterColumns,
        dateFrom || undefined,
        dateTo || undefined,
      ),
    getSumForFinance: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      filterText?: string,
      filterColumns?: string,
      dateFrom?: Date | null,
      dateTo?: Date | null,
    ) => {
      const data = await api.projectControllerGetSumForFinance(
        filterText || '',
        filterColumns,
        dateFrom || undefined,
        dateTo || undefined,
      );
      return getSummary(t, tk, data);
    },
    create: (dto: CreateUpdateProjectWithTeamDto) =>
      api.projectControllerCreate(dto),
    update: (id: string, dto: CreateUpdateProjectWithTeamDto) =>
      api.projectControllerUpdate(id, dto),
    isDelayed: async (date: Date) => await api.projectControllerIsDelayed(date),
    findInternalProjectsByPurchaserForCompanyAndTeams: (
      purchaserId: string,
      teamsIds: string[],
    ) =>
      api.projectControllerInternalProjectsByPurchaserForCompanyAndTeams(
        purchaserId,
        teamsIds,
      ),
    getWithParent: (id: string) => api.projectControllerGetWithParent(id),
    getLastEditor: (id: string) => api.projectControllerGetLastEditor(id),
    getUniqueServicesNames: (id: string) =>
      api.projectControllerUniqueServicesNames(id),
    clone: (clone: CloneProjectDto) => api.projectControllerClone(clone),
    getAllByFilter: async (text?: string, companyId?: string) =>
      api.projectControllerGetAllByCompany(
        defaultSelectorLimit,
        text,
        companyId,
      ),
    getWarningsWithFVZ: (id: string, month?: Date, profileId?: string) =>
      api.projectControllerGetWarningsWithFVZ(id, month, profileId),
    isApplicationRequiredForDateChange: (
      project: CreateUpdateProjectWithTeamDto,
      startDate: Date | undefined,
      endDate: Date | undefined,
    ) =>
      api
        .projectControllerIsApplicationRequiredForDateChange(
          project,
          startDate,
          endDate,
        )
        .then(res => res.toString() === 'true'),
  };
};

export const newProject = (
  companyId?: string,
  countryDictionaryId?: string,
) => {
  return CreateUpdateProjectWithTeamDtoFromJSON({
    companyId: companyId,
    name: '',
    teamIds: [],
    projectCountriesIds: countryDictionaryId ? [countryDictionaryId] : [],
  });
};

export const newCloneProject = (
  projectId: string,
  name: string,
  notes: string,
  withAmounts: boolean,
) => {
  return CloneProjectDtoFromJSON({
    sourceProjectId: projectId,
    name: name,
    notes: notes,
    withAmounts: withAmounts,
  });
};

export const validate = (project: CreateUpdateProjectWithTeamDto) => {
  return validateAgainst(ProjectSchema, project);
};

export const validateCloneProject = (project: CloneProjectDto) => {
  return validateAgainst(CloneProjectSchema, project);
};

export const convert = (
  project?: GetProjectDto,
  companyId?: string,
  countryDictionaryId?: string,
) => {
  if (!project) {
    return newProject(companyId, countryDictionaryId);
  }
  const converted = CreateUpdateProjectWithTeamDtoFromJSON(project);
  converted.companyId = project.company?.id;
  converted.teamIds = project.projectTeams?.map(pt => pt.teamId) || [];
  converted.purchaserId = project.purchaser?.id;
  converted.brandId = project.brand?.id;
  converted.parentId = project.parent?.id;
  converted.projectCountriesIds = project.projectCountries?.map(
    pc => pc.countryDictionaryId,
  );
  converted.projectPathPhasesIds = project.projectPathPhases?.map(
    pp => pp.pathPhaseDictionaryId,
  );
  converted.responsibleOwnerId = project.responsibleOwner?.id;
  converted.labelDictionaryId = project.labelDictionary?.id;
  converted.mainTeamId = project.projectTeams?.filter(o => o.main)[0]?.teamId;
  return converted;
};

export const convertToCreateFromOrder = (
  order: GetOrderDto,
  companyId: string,
  pathPhaseEnabled: boolean,
): CreateUpdateProjectWithTeamDto => {
  return CreateUpdateProjectWithTeamDtoFromJSON({
    name: order.project.name,
    startDate: order.project.startDate,
    endDate: order.project.endDate,
    companyId,
    brandId: order.project.brand?.id,
    parentId: order.project.id,
    teamIds: [order.team.id],
    projectCountriesIds: order.project.projectCountries?.map(
      o => o.countryDictionaryId,
    ),
    projectPathPhasesIds:
      pathPhaseEnabled && order.pathPhaseDictionary?.id
        ? [order.pathPhaseDictionary.id]
        : [],
    active: true,
  });
};

export const createNewAttachment = (id?: string) => {
  const attachment = AttachmentContext.newAttachment();
  attachment.enObjectType = AttachmentObjectType.Project;
  attachment.objectId = id as string;
  return attachment;
};

export const forProjectView = async (
  pok: PokCoreContextType,
  notifications: NotificationsProviderType,
  projectId: string,
  budgetId: string,
  companyId: string,
  action: () => void,
) => {
  const projectAttachments = await pok.attachments.getAllForObject(
    AttachmentObjectType.Project,
    projectId,
  );
  const budgetAttachments = await pok.attachments.getAllForObjectInCompany(
    AttachmentObjectType.Budget,
    budgetId,
    companyId,
  );
  const contractAttachments =
    await pok.attachments.getAllWithContractForProject(projectId);

  const attachments = [
    ...projectAttachments,
    ...budgetAttachments,
    ...contractAttachments,
  ];
  const ac = AttachmentContext.forAssociatedDocuments;

  const assocAttachments = ac(pok, notifications, attachments, action);

  assocAttachments.new = () => createNewAttachment(projectId as string);
  return {
    attachments: assocAttachments,
  };
};

export const isPendingDateChangeApplication = (
  project: CreateUpdateProjectWithTeamDto | GetProjectDto,
) => {
  if (
    project?.dateChangeVerification &&
    project?.startDate &&
    project?.endDate &&
    project?.dateChangeRequestedByEmployeeId &&
    ((project?.newStartDate &&
      project?.startDate.getDate() !== project?.newStartDate.getDate()) ||
      (project?.newEndDate &&
        project?.endDate.getDate() !== project?.newEndDate.getDate()))
  ) {
    return true;
  }
  return false;
};
